import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { diagnosisService } from '@/services/diagnosisService';
import { DiagnosisForm } from './DiagnosisForm';
import { Diagnosis } from '@/types/diagnosis.types';
import {
  Plus,
  Edit,
  Trash2,
  Crown,
  AlertCircle,
  Loader2,
  Calendar,
  Stethoscope,
  HeartPulse,
} from 'lucide-react';

interface DiagnosticSectionProps {
  patientId: number;
  isReadOnly?: boolean;
  onDiagnosesChange?: (diagnoses: Diagnosis[]) => void;
}

export const DiagnosticSection: React.FC<DiagnosticSectionProps> = ({
  patientId,
  isReadOnly = false,
  onDiagnosesChange,
}) => {
  const { toast } = useToast();
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDiagnosis, setEditingDiagnosis] = useState<Diagnosis | null>(null);

  // Load diagnoses
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const diagnosesData = await diagnosisService.getDiagnosesByPatient(patientId);

      // Ensure diagnoses is always an array
      const safeDiagnoses = Array.isArray(diagnosesData) ? diagnosesData : [];
      setDiagnoses(safeDiagnoses);
      onDiagnosesChange?.(safeDiagnoses);
    } catch (error: any) {
      console.error('Error loading diagnostic data:', error);
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-au putut încărca datele diagnostice.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [patientId, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle diagnosis operations
  const handleAddDiagnosis = () => {
    if (isReadOnly) return;
    setEditingDiagnosis(null);
    setIsDialogOpen(true);
  };

  const handleEditDiagnosis = (diagnosis: Diagnosis) => {
    if (isReadOnly) return;
    setEditingDiagnosis(diagnosis);
    setIsDialogOpen(true);
  };

  const handleDeleteDiagnosis = async (id: number) => {
    if (isReadOnly) return;
    
    // Add confirmation dialog
    if (!window.confirm('Sigur doriți să ștergeți acest diagnostic?')) {
      return;
    }
    
    try {
      await diagnosisService.deleteDiagnosis(id);
      toast({
        title: 'Succes',
        description: 'Diagnosticul a fost șters cu succes.',
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut șterge diagnosticul.',
        variant: 'destructive',
      });
    }
  };

  const handleDiagnosisSubmit = async (data: any) => {
    try {
      if (editingDiagnosis) {
        await diagnosisService.updateDiagnosis(editingDiagnosis.id, data);
      } else {
        await diagnosisService.createDiagnosis(data);
      }
      
      setIsDialogOpen(false);
      setEditingDiagnosis(null);
      await loadData();
    } catch (error: any) {
      throw error; // Let DiagnosisForm handle the error display
    }
  };

  const handleSetPrimary = async (id: number) => {
    if (isReadOnly) return;
    
    try {
      await diagnosisService.setPrimaryDiagnosis(id);
      toast({
        title: 'Succes',
        description: 'Diagnosticul a fost setat ca principal.',
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut seta diagnosticul ca principal.',
        variant: 'destructive',
      });
    }
  };

  // Get severity level color and label
  const getSeverityBadge = (level: number) => {
    const configs = {
      1: { color: 'bg-green-100 text-green-800', label: 'Ușor' },
      2: { color: 'bg-yellow-100 text-yellow-800', label: 'Moderat' },
      3: { color: 'bg-orange-100 text-orange-800', label: 'Sever' },
      4: { color: 'bg-red-100 text-red-800', label: 'Critic' },
      5: { color: 'bg-purple-100 text-purple-800', label: 'Foarte critic' },
    };
    
    const config = configs[level as keyof typeof configs] || configs[3];
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Table columns configuration
  const columns = [
    {
      name: 'Cod ICD-10',
      selector: (row: Diagnosis) => row.icd10_code,
      sortable: true,
      width: '120px',
      cell: (row: Diagnosis) => (
        <span className="font-mono font-medium">{row.icd10_code}</span>
      ),
    },
    {
      name: 'Diagnostic',
      selector: (row: Diagnosis) => row.diagnosis_name_ro || row.diagnosis_name_en || row.icd10_code,
      sortable: true,
      grow: 2,
      cell: (row: Diagnosis) => (
        <div>
          <div className="font-medium">{row.diagnosis_name_ro || row.diagnosis_name_en || row.icd10_code}</div>
          {row.eurocat_subgroup && (
            <div className="text-sm text-muted-foreground mt-1">
              EUROCAT: {row.eurocat_subgroup}
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'Principal',
      selector: (row: Diagnosis) => row.is_primary,
      sortable: true,
      width: '100px',
      cell: (row: Diagnosis) => (
        <div className="flex items-center justify-center">
          {row.is_primary ? (
            <Badge className="bg-blue-100 text-blue-800">
              <Crown className="h-3 w-3 mr-1" />
              Principal
            </Badge>
          ) : (
            !isReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleSetPrimary(row.id)}
                className="text-xs"
              >
                Setează
              </Button>
            )
          )}
        </div>
      ),
    },
    {
      name: 'Severitate',
      selector: (row: Diagnosis) => row.severity_level || row.severity_id || 3,
      sortable: true,
      width: '120px',
      cell: (row: Diagnosis) => getSeverityBadge(row.severity_level || row.severity_id || 3),
    },
    {
      name: 'Data diagnostic',
      selector: (row: Diagnosis) => row.diagnosis_date,
      sortable: true,
      width: '130px',
      cell: (row: Diagnosis) => (
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">
            {diagnosisService.formatDiagnosisDate(row.diagnosis_date)}
          </span>
        </div>
      ),
    },
    {
      name: 'Acțiuni',
      button: true,
      width: '120px',
      cell: (row: Diagnosis) => (
        !isReadOnly && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleEditDiagnosis(row)}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteDiagnosis(row.id)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )
      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#f8fafc',
        color: '#475569',
        paddingTop: '9px',
        paddingBottom: '9px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingTop: '6px',
        paddingBottom: '6px',
      },
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stethoscope className="h-5 w-5 text-blue-600" />
            Diagnostice MCC
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Se încarcă diagnosticele...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      {/* Diagnoses table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5 text-blue-600" />
              Diagnostice MCC
              {diagnoses.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {diagnoses.length} {diagnoses.length === 1 ? 'diagnostic' : 'diagnostice'}
                </Badge>
              )}
            </CardTitle>
            {!isReadOnly && (
              <Button type="button" size="sm" onClick={handleAddDiagnosis}>
                <Plus className="h-4 w-4 mr-2" />
                Adaugă diagnostic
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          {diagnoses.length === 0 ? (
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-dashed bg-muted/20 px-4 py-3"
            >
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Niciun diagnostic înregistrat</p>
                <p className="text-sm text-muted-foreground">
                  Folosiți acțiunea de mai sus pentru a adăuga unul.
                </p>
              </div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={Array.isArray(diagnoses) ? diagnoses : []}
              customStyles={customStyles}
              highlightOnHover
              responsive
              noDataComponent={
                <div className="text-center py-4 text-muted-foreground">
                  Nu există diagnostice de afișat
                </div>
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Diagnosis Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="flex max-h-[88vh] w-[min(94vw,48rem)] max-w-3xl flex-col overflow-hidden p-0">
          <DialogHeader className="border-b px-6 pb-4 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="h-5 w-5 text-blue-600" />
              {editingDiagnosis ? 'Editează diagnostic MCC' : 'Adaugă diagnostic MCC'}
            </DialogTitle>
            <DialogDescription>
              {editingDiagnosis 
                ? 'Modificați informațiile diagnosticului selectat.'
                : 'Completați informațiile pentru noul diagnostic MCC.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto px-6 py-5" onSubmit={(event) => event.stopPropagation()}>
            <DiagnosisForm
              patientId={patientId}
              diagnosis={editingDiagnosis}
              onSubmit={handleDiagnosisSubmit}
              onCancel={() => {
                setIsDialogOpen(false);
                setEditingDiagnosis(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
